import DataManager from 'utils/DataManager/dataManager';
import { ICycleBudget } from 'typings/cycleBudget';
import { ICycleBudgetField } from 'typings/cycleBudgetField';
import { AmountCode } from 'typings/enums';
import { IBudgetServiceManager, ICycleBudgetFieldValue, IComposedCycleAmount } from 'typings/dataManagers';

class BudgetServiceManager extends DataManager implements IBudgetServiceManager {

    async insertBudget(masterAccountId: string, cycleId: number, budget: ICycleBudget): Promise<Nullable<number>> {
        // tslint:disable-next-line:no-any Need to upgrade mobx before this gets fixed
        const newBudget = Object.assign({}, budget) as any;
        newBudget.cycleBudgetFields =
            budget.cycleBudgetFields.map(budgetField => this.composeCycleAmount(budgetField));
        const receive = () => this._postDataRequest<typeof newBudget, number>(
            `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/configuration/budget`, newBudget
        );
        return await this.getData<number>(receive, false);
    }

    async updateBudget(masterAccountId: string, cycleId: number, budget: ICycleBudget): Promise<Nullable<number>> {
        // tslint:disable-next-line:no-any Need to upgrade mobx before this gets fixed
        const updatedBudget = Object.assign({}, budget) as any;
        updatedBudget.cycleBudgetFields =
            budget.cycleBudgetFields.map(budgetField => this.composeCycleAmount(budgetField));
        const receive = () => this._putDataRequest<typeof updatedBudget, number>(
            `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/configuration/budget`, updatedBudget
        );
        return await this.getData<number>(receive, false);
    }

    async updateBudgetProration(
        masterAccountId: string,
        cycleId: number,
        budgetCycleId: number,
        useProration: boolean): Promise<void> {
        const receive = () =>
            this._putDataRequest<boolean, void>(
                // tslint:disable-next-line:max-line-length
                `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/configuration/budget/${budgetCycleId}/proration`,
                useProration
            );
        await this.getData<void>(receive, false);
    }

    async deleteBudget(masterAccountId: string, cycleId: number, budgetCycleId: number): Promise<void> {
        const receive = () =>
            this._deleteDataRequest<void>(
                `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/configuration/budget/${budgetCycleId}`);
        await this.getData<void>(receive, false);
    }

    async setBudgetAutoSyncAsync(
        masterAccountId: string,
        cycleId: number,
        budgetCycleId: number,
        autoSync: boolean): Promise<void> {
        const receive = () =>
            this._putDataRequest<boolean, void>(
                // tslint:disable-next-line:max-line-length
                `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/configuration/budget/${budgetCycleId}/autosync`,
                autoSync
            );
        await this.getData<void>(receive, false);
    }

    // tslint:disable:parameters-max-number
    async exportCountryBudgetToCsv(
        masterAccountId: string,
        cycleId: number,
        budgetCycleId: number,
        sourceCountry: string,
        targetCurrency: string,
        multiCountry: boolean,
        getAll: boolean,
        csvFilename: string,
        selectedGroupByFlag: string
    ): Promise<void> {
        const sourceCountryCode = sourceCountry ? `&sourceCountryCode=${sourceCountry}` : '';
        const targetCurrencyCode = targetCurrency ? `&targetCurrency=${targetCurrency}` : '';
        const getAllData = `&getAll=${getAll}`;
        const multi = `?multiCountry=${multiCountry}`;
        const groupByFlag = `&groupByFlag=${selectedGroupByFlag}`;
        const queryStringOptions = `${multi}${sourceCountryCode}${targetCurrencyCode}${getAllData}${groupByFlag}`;
        const receive = () =>
            this._downloadDataRequest(
                // tslint:disable-next-line:max-line-length
                `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/budgets/${budgetCycleId}/csv${queryStringOptions}`,
                csvFilename,
                'GET');
        await this.getData<void>(receive, false);
    }

    composeCycleAmount(cycleAmount: ICycleBudgetField): IComposedCycleAmount {
        return {
            amountCycleId: cycleAmount.amountCycleId,
            amountCode: cycleAmount.amountCode,
            budgetCycleId: cycleAmount.budgetCycleId,
            value: this.getCycleAmountValue(cycleAmount)
        };
    }

    getCycleAmountValue(cycleAmount: ICycleBudgetField): string {
        const cycleAmountValue = {} as ICycleBudgetFieldValue;

        if (cycleAmount.amountCode === AmountCode.Bonus ||
            cycleAmount.amountCode === AmountCode.VariablePay) {
            cycleAmountValue.percentageCalculationRule = cycleAmount.percentageCalculationRule;
        }

        if (cycleAmount.amountCode === AmountCode.LumpSum) {
            cycleAmountValue.rangeEnforcement = cycleAmount.rangeEnforcement;
        }

        if (cycleAmount.amountCode === AmountCode.Stock) {
            cycleAmountValue.stock = {
                displayType: cycleAmount.stockDisplayType,
                value: cycleAmount.stockValue ?? 0
            };
        }

        return JSON.stringify(cycleAmountValue);
    }
}

export { BudgetServiceManager };
export default BudgetServiceManager;